/**
 * Video Hero Module
 * ─────────────────────────────────────────────
 * Full-width video hero with responsive sources
 * (portrait for mobile, landscape for desktop).
 *
 * Only ONE video plays at any time. The inactive
 * video is paused to avoid double-audio echo.
 * On resize (breakpoint cross) the active video
 * switches seamlessly.
 *
 * When the hero scrolls out of the viewport the
 * video is paused to save resources; it resumes
 * automatically on scroll-back.
 *
 * Behaviour:
 *   • Autoplay muted on load (only the visible video).
 *   • 1st click/tap → unmute + restart from 0:00.
 *   • 2nd click/tap → mute (no restart).
 *   • 3rd click/tap → unmute (no restart).
 *   • Desktop: sound icon visible on hover only
 *     (always visible when muted).
 *   • Mobile: sound icon always visible when muted.
 */

(function () {
    'use strict';

    var wrapper = document.querySelector('.video-hero');
    if (!wrapper) return;

    var desktopVideo = wrapper.querySelector('.video-hero__video--desktop');
    var mobileVideo = wrapper.querySelector('.video-hero__video--mobile');
    var soundBtn = wrapper.querySelector('.video-hero__sound-btn');
    var fullscreenBtn = wrapper.querySelector('.video-hero__fullscreen-btn');
    var subsBtn = wrapper.querySelector('.video-hero__subs-btn');
    var subsMenu = wrapper.querySelector('.video-hero__subs-menu');
    var subsOptions = wrapper.querySelectorAll('.video-hero__subs-option');
    var kebabBtn = wrapper.querySelector('.video-hero__kebab-btn');
    var controls = wrapper.querySelector('.video-hero__controls');

    if (!soundBtn) return;

    var BREAKPOINT = 769;

    // Track state
    var hasEverUnmuted = false;
    var isMuted = true;
    var isInViewport = true;
    var currentActive = null; // reference to the currently playing video
    var activeSubsTrack = 'off'; // 'off', 'subtitles', 'sdh'

    /**
     * Determine which video should be active based on viewport.
     */
    function getTargetVideo() {
        return window.innerWidth >= BREAKPOINT ? desktopVideo : mobileVideo;
    }

    /**
     * Activate the correct video and pause the other.
     * Syncs currentTime so the switch feels seamless.
     */
    function activateVideo() {
        var target = getTargetVideo();
        if (target === currentActive) return; // nothing to do

        var other = (target === desktopVideo) ? mobileVideo : desktopVideo;

        // Sync time from old to new (if old was playing)
        if (currentActive && target) {
            target.currentTime = currentActive.currentTime;
        }

        // Pause the other video
        if (other) {
            other.pause();
            other.muted = true; // always mute the inactive one
        }

        // Play the target only if in viewport
        if (target && isInViewport) {
            target.muted = isMuted;
            safePlay(target);
        }

        currentActive = target;
    }

    /**
     * Safe play helper — catches autoplay rejections.
     */
    function safePlay(video) {
        var p = video.play();
        if (p !== undefined) {
            p.catch(function () { /* autoplay blocked — OK */ });
        }
    }

    /**
     * Pause the active video (viewport exit).
     */
    function pauseActive() {
        if (currentActive) {
            currentActive.pause();
        }
    }

    /**
     * Resume the active video (viewport enter).
     */
    function resumeActive() {
        if (currentActive) {
            currentActive.muted = isMuted;
            safePlay(currentActive);
        }
    }

    /**
     * Update mute state on the currently active video only.
     */
    function setMuted(muted) {
        isMuted = muted;

        if (currentActive) {
            currentActive.muted = muted;
        }

        if (muted) {
            wrapper.classList.remove('is-unmuted');
            wrapper.classList.add('is-muted');
        } else {
            wrapper.classList.remove('is-muted');
            wrapper.classList.add('is-unmuted');
        }
    }

    /**
     * Disable all text tracks on a video element.
     */
    function disableAllTracks(video) {
        if (!video || !video.textTracks) return;
        for (var i = 0; i < video.textTracks.length; i++) {
            video.textTracks[i].mode = 'disabled';
        }
    }

    /**
     * Position all cues of a text track above the bottom controls.
     */
    function positionCues(textTrack) {
        var cues = textTrack.cues;
        if (!cues) return;
        for (var j = 0; j < cues.length; j++) {
            cues[j].snapToLines = false;
            cues[j].line = 88; // 70% from top → 30% from bottom
        }
    }

    /**
     * Enable a specific track on a video by data-track-id.
     * Handles async VTT loading — positions cues once they are available.
     */
    function enableTrack(video, trackId) {
        if (!video || !video.textTracks) return;
        var trackEls = video.querySelectorAll('track');
        for (var i = 0; i < trackEls.length; i++) {
            if (trackEls[i].getAttribute('data-track-id') === trackId) {
                var tt = video.textTracks[i];
                tt.mode = 'showing';

                // Try positioning immediately
                if (tt.cues && tt.cues.length > 0) {
                    positionCues(tt);
                } else {
                    // VTT not loaded yet — wait for load event on <track> element
                    (function (trackEl, textTrack) {
                        trackEl.addEventListener('load', function onLoad() {
                            positionCues(textTrack);
                            trackEl.removeEventListener('load', onLoad);
                        });
                    })(trackEls[i], tt);
                }
            } else {
                video.textTracks[i].mode = 'disabled';
            }
        }
    }

    /**
     * Apply current subtitle state to both videos.
     */
    function applySubs() {
        if (activeSubsTrack === 'off') {
            disableAllTracks(desktopVideo);
            disableAllTracks(mobileVideo);
            wrapper.classList.remove('has-subs');
        } else {
            enableTrack(desktopVideo, activeSubsTrack);
            enableTrack(mobileVideo, activeSubsTrack);
            wrapper.classList.add('has-subs');
        }

        // Update menu active state
        for (var i = 0; i < subsOptions.length; i++) {
            var opt = subsOptions[i];
            if (opt.getAttribute('data-subs') === activeSubsTrack) {
                opt.classList.add('is-active');
            } else {
                opt.classList.remove('is-active');
            }
        }
    }

    /**
     * Toggle subtitles menu visibility.
     */
    function toggleSubsMenu(e) {
        e.preventDefault();
        e.stopPropagation();
        var isOpen = subsMenu.classList.toggle('is-open');
        subsBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    /**
     * Close subtitles menu.
     */
    function closeSubsMenu() {
        subsMenu.classList.remove('is-open');
        subsBtn.setAttribute('aria-expanded', 'false');
    }

    /**
     * Handle subtitle option selection.
     */
    function handleSubsOption(e) {
        e.preventDefault();
        e.stopPropagation();
        activeSubsTrack = e.currentTarget.getAttribute('data-subs');
        applySubs();
        closeSubsMenu();
    }

    /**
     * Handle click/tap on the video wrapper (mute toggle).
     */
    function handleToggle(e) {
        e.preventDefault();

        if (!currentActive) return;

        if (isMuted) {
            // Currently muted → unmute
            if (!hasEverUnmuted) {
                // First time unmuting → restart from beginning
                hasEverUnmuted = true;
                currentActive.currentTime = 0;
            }
            setMuted(false);
        } else {
            // Currently unmuted → mute
            setMuted(true);
        }
    }

    /**
     * Enter native fullscreen with sound.
     */
    function handleFullscreen(e) {
        e.preventDefault();
        e.stopPropagation(); // don't trigger wrapper click (mute toggle)

        if (!currentActive) return;

        // Unmute for fullscreen
        setMuted(false);
        hasEverUnmuted = true;

        // Request fullscreen on the video element
        if (currentActive.requestFullscreen) {
            currentActive.requestFullscreen();
        } else if (currentActive.webkitRequestFullscreen) {
            currentActive.webkitRequestFullscreen();
        } else if (currentActive.webkitEnterFullScreen) {
            // iOS Safari: works on <video> directly
            currentActive.webkitEnterFullScreen();
        }
    }

    /**
     * When exiting fullscreen, restore mute state so
     * the inline video doesn't blast audio unexpectedly.
     */
    function onFullscreenChange() {
        var isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
        if (!isFullscreen) {
            // Left fullscreen → mute inline playback
            setMuted(true);
        }
    }

    /**
     * Debounced resize handler to switch videos at breakpoint.
     */
    var resizeTimer;
    function onResize() {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
            activateVideo();
        }, 150);
    }

    /**
     * Toggle kebab menu (mobile controls expand/collapse).
     */
    function toggleKebab(e) {
        e.preventDefault();
        e.stopPropagation();
        if (!controls) return;
        var isOpen = controls.classList.toggle('is-expanded');
        kebabBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    /**
     * Close kebab menu.
     */
    function closeKebab() {
        if (!controls) return;
        controls.classList.remove('is-expanded');
        if (kebabBtn) kebabBtn.setAttribute('aria-expanded', 'false');
    }

    /**
     * Initialise the module.
     */
    function init() {
        // Set initial state
        wrapper.classList.add('is-muted');

        // Disable all text tracks by default
        disableAllTracks(desktopVideo);
        disableAllTracks(mobileVideo);

        // Bind click on the entire wrapper (mute toggle + close menus)
        wrapper.addEventListener('click', function (e) {
            // Close subs menu on any wrapper click
            if (subsMenu && subsMenu.classList.contains('is-open')) {
                closeSubsMenu();
                return;
            }
            // Close kebab on any wrapper click
            if (controls && controls.classList.contains('is-expanded')) {
                closeKebab();
                return;
            }
            handleToggle(e);
        });

        // Sound button: stop propagation so it doesn't double-fire with wrapper
        if (soundBtn) {
            soundBtn.addEventListener('click', function (e) {
                e.stopPropagation();
                handleToggle(e);
            });
        }

        // Subtitles button + menu
        if (subsBtn && subsMenu) {
            subsBtn.addEventListener('click', toggleSubsMenu);

            for (var i = 0; i < subsOptions.length; i++) {
                subsOptions[i].addEventListener('click', handleSubsOption);
            }

            // Close menu on click outside
            document.addEventListener('click', function (e) {
                if (!subsMenu.contains(e.target) && !subsBtn.contains(e.target)) {
                    closeSubsMenu();
                }
            });
        }

        // Kebab button (mobile)
        if (kebabBtn) {
            kebabBtn.addEventListener('click', toggleKebab);
        }

        // Fullscreen button
        if (fullscreenBtn) {
            fullscreenBtn.addEventListener('click', handleFullscreen);
        }

        // Restore mute on fullscreen exit
        document.addEventListener('fullscreenchange', onFullscreenChange);
        document.addEventListener('webkitfullscreenchange', onFullscreenChange);

        // Start only the correct video, pause the other
        activateVideo();

        // Listen for viewport changes
        window.addEventListener('resize', onResize, { passive: true });

        // Pause/resume when scrolled out of viewport
        var observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                isInViewport = entry.isIntersecting;
                if (isInViewport) {
                    resumeActive();
                } else {
                    pauseActive();
                }
            });
        }, {
            root: null,
            threshold: 0.05 // ~5% visible triggers play
        });

        observer.observe(wrapper);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
